import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import dayjs, { Dayjs } from 'dayjs';

import {
  ARROW_NAV_FETCH_THRESHOLD_DAYS,
  COMPENSATION_TOLERANCE_CELLS,
  FIRST_VISIBLE_DATE_INIT_DELAY_MS,
  HORIZONTAL_WINDOW_SHIFT_DAYS,
  MAX_DATES_WINDOW,
  PLANNER_PAGINATION_LOG_TAG,
  PLANNER_SCROLL_RESTORE_FALLBACK_MS,
  PROGRAMMATIC_SCROLL_GRACE_COMPENSATION_MS,
  PROGRAMMATIC_SCROLL_GRACE_EXPANDED_MS,
} from '@/modules/planner';
import { logger } from '@/utils/logger/logger';

import {
  DateRelation,
  getDateRelationToLoadedRange,
} from '../../helpers/planner-base/planner-pagination-helpers';
import {
  clampDateIndex,
  getDefaultLeftLoadTriggerPx,
  getDefaultRightLoadTriggerPx,
  getViewportIndices,
} from '../../helpers/scroll/planner-scroll';
import { scrollToDateInstant } from '../../helpers/scroll/scroll-to-date';
import usePlannerStore from '../../store';
import { useExpandedWindowInteractionTracking } from './use-expanded-window-interaction-tracking';

interface UsePlannerHorizontalLifecycleParams {
  parentRef: RefObject<HTMLDivElement | null>;
  rowsLength: number;
  cellWidthPx: number;
  dates: Dayjs[];
  isLoading: boolean;
  /** Placeholder-data refresh (keepPreviousData) — a filter reload flips this, not isLoading */
  isRefreshing?: boolean;
  onLoadDatesLeft?: () => Promise<void>;
  onLoadDatesRight?: () => Promise<void>;
  normalizeDatesWindow: (anchorDate: Dayjs) => void;
  pendingScrollTarget: Dayjs | null;
  setPendingScrollTarget: (date: Dayjs | null) => void;
  updateVisibleDateRange: (force?: boolean) => void;
  updateFirstVisibleDate: (force?: boolean) => void;
  startAsyncLeftFetch: (reason: string) => void;
  startAsyncRightFetch: (reason: string) => void;
  refs: {
    lastScrollLeftRef: RefObject<number>;
    isExtendingLeftRef: RefObject<boolean>;
    isExtendingRightRef: RefObject<boolean>;
    isLoadingLeftAsyncRef: RefObject<boolean>;
    isLoadingRightAsyncRef: RefObject<boolean>;
    pendingLeftCompensationRef: RefObject<number | null>;
    pendingRightCompensationRef: RefObject<number | null>;
    nextLeftLoadTriggerScrollLeftRef: RefObject<number | null>;
    nextRightLoadTriggerScrollLeftRef: RefObject<number | null>;
    pendingLeftAsyncFetchRef: RefObject<boolean>;
    pendingRightAsyncFetchRef: RefObject<boolean>;
    paginationSettleRafRef: RefObject<number | null>;
    programmaticHorizontalScrollUntilRef: RefObject<number>;
    isMouseButtonDownRef: RefObject<boolean>;
    pendingNormalizedScrollLeftRef: RefObject<number | null>;
    pendingExpandedLeftInteractionScrollLeftRef: RefObject<number | null>;
    expandedWindowDuringInteractionRef: RefObject<boolean>;
  };
}

interface UsePlannerHorizontalLifecycleResult {
  normalizeExpandedDatesWindow: () => boolean;
}

export const usePlannerHorizontalLifecycle = ({
  parentRef,
  rowsLength,
  cellWidthPx,
  dates,
  isLoading,
  isRefreshing = false,
  onLoadDatesLeft,
  onLoadDatesRight,
  normalizeDatesWindow,
  pendingScrollTarget,
  setPendingScrollTarget,
  updateVisibleDateRange,
  updateFirstVisibleDate,
  startAsyncLeftFetch,
  startAsyncRightFetch,
  refs,
}: UsePlannerHorizontalLifecycleParams): UsePlannerHorizontalLifecycleResult => {
  const sawLoadingRef = useRef(false);
  const hasInitializedInitialScrollRef = useRef(false);
  const sawLoadingForRestoreRef = useRef(false);
  const pendingScrollRestoreLeft = usePlannerStore(store => store.pendingScrollRestoreLeft);
  const setPendingScrollRestoreLeft = usePlannerStore(store => store.setPendingScrollRestoreLeft);

  const normalizeExpandedDatesWindow = useCallback((): boolean => {
    const container = parentRef.current;

    if (!container || !refs.expandedWindowDuringInteractionRef.current || cellWidthPx === 0) {
      return false;
    }

    const anchorIndex = clampDateIndex(
      Math.floor(container.scrollLeft / cellWidthPx),
      dates.length
    );
    const anchorDate = dates[anchorIndex];
    const offsetWithinCell = container.scrollLeft - anchorIndex * cellWidthPx;
    const centeredIndex = Math.floor(MAX_DATES_WINDOW / 2);

    refs.pendingNormalizedScrollLeftRef.current = centeredIndex * cellWidthPx + offsetWithinCell;
    refs.expandedWindowDuringInteractionRef.current = false;
    normalizeDatesWindow(anchorDate);

    return true;
  }, [cellWidthPx, dates, normalizeDatesWindow, parentRef, refs]);

  useExpandedWindowInteractionTracking({
    isMouseButtonDownRef: refs.isMouseButtonDownRef,
    normalizeExpandedDatesWindow,
  });

  useEffect(() => {
    const container = parentRef.current;
    if (!container) {
      return;
    }

    const handleScrollAnimationComplete = () => {
      updateVisibleDateRange(true);
      updateFirstVisibleDate(true);
      refs.lastScrollLeftRef.current = container.scrollLeft;

      const {
        queryDateRanges,
        groupBy: activeGroupBy,
        dates: storeDates,
        firstVisibleDate,
      } = usePlannerStore.getState();
      const queryDateRange = queryDateRanges[activeGroupBy];

      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        'scrollAnimationComplete: firstVisible=' +
          firstVisibleDate?.format('YYYY-MM-DD') +
          ' queryRange=' +
          queryDateRange?.startDate +
          '..' +
          queryDateRange?.endDate +
          ' dates=' +
          storeDates.length +
          ' loadingLeft=' +
          refs.isLoadingLeftAsyncRef.current +
          ' loadingRight=' +
          refs.isLoadingRightAsyncRef.current
      );

      if (!queryDateRange || cellWidthPx <= 0 || storeDates.length === 0) {
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          'scrollAnimationComplete: skipped edge check hasRange=' +
            Boolean(queryDateRange) +
            ' cellWidthPx=' +
            cellWidthPx +
            ' dates=' +
            storeDates.length
        );
        return;
      }

      const viewportIndices = getViewportIndices({
        scrollLeft: container.scrollLeft,
        clientWidth: container.clientWidth,
        cellWidthPx,
      });
      const leftDate = storeDates[clampDateIndex(viewportIndices.startIndex, storeDates.length)];
      const rightDate = storeDates[clampDateIndex(viewportIndices.endIndex, storeDates.length)];
      const leftRelation = getDateRelationToLoadedRange(leftDate, queryDateRange);
      const rightRelation = getDateRelationToLoadedRange(rightDate, queryDateRange);
      const leftDaysFromStart = leftDate
        ? leftDate.diff(dayjs(queryDateRange.startDate), 'day')
        : Infinity;
      const rightDaysFromEnd = rightDate
        ? dayjs(queryDateRange.endDate).diff(rightDate, 'day')
        : Infinity;
      const needsLeftFetch =
        leftRelation === DateRelation.Before ||
        (leftRelation === DateRelation.NearStart &&
          leftDaysFromStart < ARROW_NAV_FETCH_THRESHOLD_DAYS);
      const needsRightFetch =
        rightRelation === DateRelation.After ||
        (rightRelation === DateRelation.NearEnd &&
          rightDaysFromEnd < ARROW_NAV_FETCH_THRESHOLD_DAYS);

      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        'scrollAnimationComplete: leftDate=' +
          leftDate?.format('YYYY-MM-DD') +
          ' rightDate=' +
          rightDate?.format('YYYY-MM-DD') +
          ' leftRel=' +
          leftRelation +
          ' rightRel=' +
          rightRelation +
          ' needsLeft=' +
          needsLeftFetch +
          ' needsRight=' +
          needsRightFetch
      );

      if (needsLeftFetch && onLoadDatesLeft && !refs.isLoadingLeftAsyncRef.current) {
        logger.debug(PLANNER_PAGINATION_LOG_TAG, 'scrollAnimationComplete: triggering LEFT fetch');
        refs.isLoadingLeftAsyncRef.current = true;
        onLoadDatesLeft().finally(() => {
          refs.isLoadingLeftAsyncRef.current = false;
          logger.debug(PLANNER_PAGINATION_LOG_TAG, 'scrollAnimationComplete: LEFT fetch completed');
        });
        return;
      }

      if (needsRightFetch && onLoadDatesRight && !refs.isLoadingRightAsyncRef.current) {
        logger.debug(PLANNER_PAGINATION_LOG_TAG, 'scrollAnimationComplete: triggering RIGHT fetch');
        refs.isLoadingRightAsyncRef.current = true;
        onLoadDatesRight().finally(() => {
          refs.isLoadingRightAsyncRef.current = false;
          logger.debug(
            PLANNER_PAGINATION_LOG_TAG,
            'scrollAnimationComplete: RIGHT fetch completed'
          );
        });
        return;
      }

      logger.debug(PLANNER_PAGINATION_LOG_TAG, 'scrollAnimationComplete: no fetch needed');
    };

    container.addEventListener('scrollAnimationComplete', handleScrollAnimationComplete);
    return () => {
      container.removeEventListener('scrollAnimationComplete', handleScrollAnimationComplete);
    };
  }, [
    cellWidthPx,
    onLoadDatesLeft,
    onLoadDatesRight,
    parentRef,
    refs,
    updateFirstVisibleDate,
    updateVisibleDateRange,
  ]);

  useLayoutEffect(() => {
    const container = parentRef.current;
    if (!container) {
      refs.isExtendingLeftRef.current = false;
      refs.isExtendingRightRef.current = false;
      return;
    }

    const chunkWidthPx = HORIZONTAL_WINDOW_SHIFT_DAYS * cellWidthPx;

    if (refs.pendingExpandedLeftInteractionScrollLeftRef.current !== null) {
      const targetScrollLeft = refs.pendingExpandedLeftInteractionScrollLeftRef.current;

      refs.programmaticHorizontalScrollUntilRef.current =
        performance.now() + PROGRAMMATIC_SCROLL_GRACE_EXPANDED_MS;
      container.scrollLeft = targetScrollLeft;
      refs.lastScrollLeftRef.current = container.scrollLeft;
      refs.pendingExpandedLeftInteractionScrollLeftRef.current = null;

      if (refs.paginationSettleRafRef.current !== null) {
        cancelAnimationFrame(refs.paginationSettleRafRef.current);
      }

      refs.paginationSettleRafRef.current = requestAnimationFrame(() => {
        refs.paginationSettleRafRef.current = null;
        updateVisibleDateRange(true);
        updateFirstVisibleDate(true);
      });
    } else if (refs.pendingNormalizedScrollLeftRef.current !== null) {
      const targetScrollLeft = refs.pendingNormalizedScrollLeftRef.current;

      refs.programmaticHorizontalScrollUntilRef.current =
        performance.now() + PROGRAMMATIC_SCROLL_GRACE_COMPENSATION_MS;
      container.scrollLeft = targetScrollLeft;
      refs.lastScrollLeftRef.current = container.scrollLeft;
      refs.pendingNormalizedScrollLeftRef.current = null;

      if (refs.paginationSettleRafRef.current !== null) {
        cancelAnimationFrame(refs.paginationSettleRafRef.current);
      }

      refs.paginationSettleRafRef.current = requestAnimationFrame(() => {
        refs.paginationSettleRafRef.current = null;

        refs.nextLeftLoadTriggerScrollLeftRef.current = getDefaultLeftLoadTriggerPx(cellWidthPx);
        refs.nextRightLoadTriggerScrollLeftRef.current = getDefaultRightLoadTriggerPx({
          datesLength: dates.length,
          cellWidthPx,
          clientWidth: container.clientWidth,
        });

        updateVisibleDateRange(true);
        updateFirstVisibleDate(true);
      });
    } else if (refs.pendingLeftCompensationRef.current !== null) {
      const triggerScrollLeft = refs.pendingLeftCompensationRef.current;
      const expectedScrollLeft = triggerScrollLeft + chunkWidthPx;

      refs.programmaticHorizontalScrollUntilRef.current =
        performance.now() + PROGRAMMATIC_SCROLL_GRACE_COMPENSATION_MS;
      container.scrollLeft = expectedScrollLeft;
      refs.lastScrollLeftRef.current = container.scrollLeft;
      refs.pendingLeftCompensationRef.current = null;

      if (refs.paginationSettleRafRef.current !== null) {
        cancelAnimationFrame(refs.paginationSettleRafRef.current);
      }

      refs.paginationSettleRafRef.current = requestAnimationFrame(() => {
        refs.paginationSettleRafRef.current = null;

        const finalScrollLeft = container.scrollLeft;
        const compensationHeld =
          Math.abs(finalScrollLeft - expectedScrollLeft) <=
          cellWidthPx * COMPENSATION_TOLERANCE_CELLS;

        refs.nextLeftLoadTriggerScrollLeftRef.current = compensationHeld
          ? triggerScrollLeft
          : Math.max(0, finalScrollLeft - chunkWidthPx);

        updateVisibleDateRange(true);
        updateFirstVisibleDate(true);

        if (refs.pendingLeftAsyncFetchRef.current) {
          refs.pendingLeftAsyncFetchRef.current = false;
          startAsyncLeftFetch('left-pagination-settle');
        }
      });
    } else if (refs.pendingRightCompensationRef.current !== null) {
      const triggerScrollLeft = refs.pendingRightCompensationRef.current;
      const expectedScrollLeft = Math.max(0, triggerScrollLeft - chunkWidthPx);

      refs.programmaticHorizontalScrollUntilRef.current =
        performance.now() + PROGRAMMATIC_SCROLL_GRACE_COMPENSATION_MS;
      container.scrollLeft = expectedScrollLeft;
      refs.lastScrollLeftRef.current = container.scrollLeft;
      refs.pendingRightCompensationRef.current = null;

      if (refs.paginationSettleRafRef.current !== null) {
        cancelAnimationFrame(refs.paginationSettleRafRef.current);
      }

      refs.paginationSettleRafRef.current = requestAnimationFrame(() => {
        refs.paginationSettleRafRef.current = null;

        const finalScrollLeft = container.scrollLeft;
        const compensationHeld =
          Math.abs(finalScrollLeft - expectedScrollLeft) <=
          cellWidthPx * COMPENSATION_TOLERANCE_CELLS;

        refs.nextRightLoadTriggerScrollLeftRef.current = compensationHeld
          ? triggerScrollLeft
          : finalScrollLeft + chunkWidthPx;

        updateVisibleDateRange(true);
        updateFirstVisibleDate(true);

        if (refs.pendingRightAsyncFetchRef.current) {
          refs.pendingRightAsyncFetchRef.current = false;
          startAsyncRightFetch('right-pagination-settle');
        }
      });
    }

    refs.isExtendingLeftRef.current = false;
    refs.isExtendingRightRef.current = false;
  }, [
    cellWidthPx,
    dates,
    parentRef,
    refs,
    startAsyncLeftFetch,
    startAsyncRightFetch,
    updateFirstVisibleDate,
    updateVisibleDateRange,
  ]);

  useEffect(() => {
    const container = parentRef.current;
    if (!container) {
      return;
    }

    if (container.dataset.scrollAnimating === 'true') {
      return;
    }

    if (usePlannerStore.getState().pendingScrollTarget) {
      return;
    }

    // A filter-change reload owns the scroll restore (pendingScrollRestoreLeft);
    // don't let this generic safety-net restore race it.
    if (usePlannerStore.getState().pendingScrollRestoreLeft !== null) {
      return;
    }

    if (container.scrollLeft === 0 && refs.lastScrollLeftRef.current > 0) {
      refs.programmaticHorizontalScrollUntilRef.current =
        performance.now() + PROGRAMMATIC_SCROLL_GRACE_COMPENSATION_MS;
      container.scrollLeft = refs.lastScrollLeftRef.current;
    }
  }, [dates.length, parentRef, refs, rowsLength]);

  useEffect(() => {
    if (!pendingScrollTarget) {
      sawLoadingRef.current = false;
      return;
    }

    if (isLoading || isRefreshing) {
      sawLoadingRef.current = true;
      return;
    }

    if (sawLoadingRef.current) {
      sawLoadingRef.current = false;
      scrollToDateInstant(pendingScrollTarget);
      setPendingScrollTarget(null);
      return;
    }

    // No busy cycle observed — the navigation resolved from cache (or the
    // reload is still pending behind the filter debounce, in which case a busy
    // flip cancels this timer). Consume the target anyway so it can't strand.
    const timeoutId = setTimeout(() => {
      if (usePlannerStore.getState().pendingScrollTarget) {
        scrollToDateInstant(pendingScrollTarget);
        setPendingScrollTarget(null);
      }
    }, PLANNER_SCROLL_RESTORE_FALLBACK_MS);
    return () => clearTimeout(timeoutId);
  }, [isLoading, isRefreshing, pendingScrollTarget, setPendingScrollTarget]);

  // Restore the EXACT scroll position after a filter-change reload (set by
  // setFilters/resetFilter). Waits for the reload's busy cycle (true→false),
  // then restores scrollLeft directly — pixel-precise, since the filter change
  // keeps the same dates window and cell width. This avoids the ~1-2 day drift a
  // date-centering restore (scrollToDateInstant) would introduce.
  //
  // "Busy" is isLoading OR isRefreshing: with placeholderData keepPreviousData a
  // filter reload only flips isRefreshing (isPlaceholderData) — isLoading
  // (isPending) stays false, which used to strand pendingScrollRestoreLeft and
  // leave the viewport wherever the reload reflow dropped it (the loaded-window
  // start, months away). A cache-hit filter change flips NEITHER flag, so a
  // timeout failsafe consumes the restore in that case — a stranded restore also
  // disables the scrollLeft===0 safety net above.
  useEffect(() => {
    if (pendingScrollRestoreLeft === null) {
      sawLoadingForRestoreRef.current = false;
      return;
    }

    if (isLoading || isRefreshing) {
      sawLoadingForRestoreRef.current = true;
      return;
    }

    const performRestore = (reason: string) => {
      sawLoadingForRestoreRef.current = false;
      const container = parentRef.current;
      if (container) {
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          `scrollRestore(${reason}): restoring scrollLeft=` +
            pendingScrollRestoreLeft +
            ' (was ' +
            container.scrollLeft +
            ')'
        );
        container.scrollLeft = pendingScrollRestoreLeft;
        refs.lastScrollLeftRef.current = pendingScrollRestoreLeft;
        updateVisibleDateRange(true);
        updateFirstVisibleDate(true);
      }
      setPendingScrollRestoreLeft(null);
    };

    if (sawLoadingForRestoreRef.current) {
      performRestore('reload-complete');
      return;
    }

    // No busy cycle observed (yet): either the reload is still pending behind
    // the filter debounce (a busy flip will cancel this timer via the effect
    // cleanup) or the change resolved from cache and no reload will ever run.
    const timeoutId = setTimeout(() => {
      if (usePlannerStore.getState().pendingScrollRestoreLeft !== null) {
        performRestore('no-reload-fallback');
      }
    }, PLANNER_SCROLL_RESTORE_FALLBACK_MS);
    return () => clearTimeout(timeoutId);
  }, [
    isLoading,
    isRefreshing,
    pendingScrollRestoreLeft,
    setPendingScrollRestoreLeft,
    parentRef,
    refs,
    updateVisibleDateRange,
    updateFirstVisibleDate,
  ]);

  useEffect(() => {
    if (rowsLength === 0 || hasInitializedInitialScrollRef.current) {
      return;
    }

    hasInitializedInitialScrollRef.current = true;

    // Use instant scroll on initial load — animated scroll after a skeleton screen looks buggy.
    // Animation is still used for user-triggered navigation (today button, date picker).
    const timeoutId = setTimeout(() => {
      scrollToDateInstant(dayjs());
    }, FIRST_VISIBLE_DATE_INIT_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [rowsLength]);

  return {
    normalizeExpandedDatesWindow,
  };
};
